import {
  BeneficialOwnerControlType,
  Shareholding,
  ShareholdingType,
  UpdateIndividualShareholdingPayload,
  UpdateInstitutionShareholdingPayload,
  useOwnership,
  useStockClassList,
  useUpdateShareholding,
} from '@commenda/api';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FieldWrapper,
  InputErrorMessage,
  Label,
  SingleCombobox,
} from '@commenda/kakashi';
import { OwnershipRelationshipType } from '@prisma/client';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useController } from 'react-hook-form';
import {
  CollapsibleModuleCard,
  FormActions,
} from '../../../form-builder/components/new-incorp-layout';
import { useZodForm } from '../../../hooks/useZodForm';
import {
  ENTITY_RELATIONSHIP_OPTIONS,
  INDIVIDUAL_RELATIONSHIP_OPTIONS,
} from '../constants';
import { buildEditStakeholderSchema } from '../schemas';
import { isCapitalOnlyLlc, useEntityOwnershipNouns } from '../ownershipNouns';
import { OwnershipPercentField } from './OwnershipPercentField';
import { applyOwnershipApiError } from './applyOwnershipApiError';
import { HscCheckbox } from './HscCheckbox';
import { SUBMIT_VALIDATION_TOAST } from './submitToast';
import { UltimateBeneficialOwnerControlTypeSection } from './UltimateBeneficialOwnerControlTypeSection';
import {
  InheritableUltimateBeneficialOwnersSection,
  type InheritedUboSelection,
} from './InheritableUltimateBeneficialOwnersSection';
import { StakeholderStockClassField } from './StakeholderStockClassField';
import { useStockClassStaging } from './useStockClassStaging';
import { useGetDilutedPercent } from './useGetDilutedPercent';
import { useHscThresholdAutoToggle } from './useHscThresholdAutoToggle';
import { useOwnershipFormDerivations } from './useOwnershipFormDerivations';
import { useUboControlTypeSync } from './useUboControlTypeSync';
import {
  formatPersonList,
  previewInheritanceLoss,
} from '../previewInheritanceLoss';

interface EditStakeholderFormProps {
  corporationId: number;
  stakeholder: Shareholding;
  percentScale: number;
  onCancel: () => void;
  onSuccess?: (message: string) => void;
  onError?: (message: string) => void;
}

export const EditStakeholderForm = ({
  corporationId,
  stakeholder,
  percentScale,
  onCancel,
  onSuccess,
  onError,
}: EditStakeholderFormProps) => {
  const isInstitution = stakeholder.type === ShareholdingType.INSTITUTION;
  const isIndividual = !isInstitution;
  // Single update endpoint addressed by stakeholdingId for both branches.
  const updateMutation = useUpdateShareholding(corporationId);
  const [cardOpen, setCardOpen] = useState(true);
  const { uboThreshold, getBudget } = useOwnershipFormDerivations(corporationId, {
    excludeStakeholdingId: stakeholder.stakeholdingId,
  });
  const nouns = useEntityOwnershipNouns(corporationId);
  // The schema needs this before the form is built.
  const { data: formStockClasses } = useStockClassList(corporationId);
  const capitalOnlyLlc = isCapitalOnlyLlc(nouns.tracksUnits, formStockClasses);
  const schema = useMemo(
    () => buildEditStakeholderSchema(percentScale, getBudget, nouns.classFieldNouns.singular, !capitalOnlyLlc),
    [percentScale, getBudget, nouns.classFieldNouns.singular, capitalOnlyLlc],
  );
  const percentStep = 1 / percentScale;

  const {
    handleSubmit,
    control,
    setError,
    watch,
    formState: { errors },
  } = useZodForm({
    schema,
    defaultValues: {
      // Null on a legacy row that never got a share class — left blank rather
      // than 0, which would read as a real zero stake.
      ownershipPercent:
        stakeholder.ownershipPercent !== null
          ? stakeholder.ownershipPercent / percentScale
          : undefined,
      hasSignificantControl: stakeholder.hasSignificantControl,
      relationshipType: stakeholder.relationshipType,
      stockClassId: stakeholder.stockClass?.id,
    },
  });

  const { field: hscField } = useController({ control, name: 'hasSignificantControl' });
  const hsc = (hscField.value as boolean | undefined) ?? false;

  const {
    stagedStockClass,
    setStagedStockClass,
    existingStockClassId,
    selectedStockClassName,
    stockClassBody,
    ownedSharesFromPercent,
  } = useStockClassStaging(corporationId, watch('stockClassId'));

  const getDilutedPercent = useGetDilutedPercent(corporationId, {
    stagedAuthorizedShares: stagedStockClass?.authorizedShares ?? null,
    excludeStakeholdingId: stakeholder.stakeholdingId,
  });
  const { aboveThreshold } = useHscThresholdAutoToggle({
    control,
    uboThresholdPercent: uboThreshold,
    getDilutedPercent,
    stockClassId: existingStockClassId ?? null,
  });

  const ubo = useUboControlTypeSync({
    hsc,
    aboveThreshold,
    initial: isIndividual ? stakeholder.controlType : null,
  });

  // Currently-inherited UBO set, derived from the page's ownership snapshot
  // (no extra fetch). Each path with sourceCorporationId === parentCorpId
  // contributes one row.
  const { data: ownership } = useOwnership(corporationId);
  const parentCorpId = stakeholder.stakeholderCorporationId;
  const initialInheritedSelections = useMemo<InheritedUboSelection[]>(() => {
    if (isIndividual || !parentCorpId) return [];
    return (ownership?.beneficialOwners ?? []).flatMap((bo) =>
      bo.paths
        .filter((p) => p.sourceCorporationId === parentCorpId)
        .map((p) => ({ directorId: bo.directorId, controlType: p.controlType })),
    );
  }, [isIndividual, parentCorpId, ownership]);
  // HSC toggles clear selections to null so the section auto-seeds (off→on)
  // or so submit sends [] (on→off → BE drops the inherited graph). BE-truth
  // changes while HSC stays on resync to the new truth (covers a UBO removed
  // via the sheet, or a background refetch); mid-flight picker edits get
  // blown away.
  const [inheritedSelections, setInheritedSelections] = useState<
    InheritedUboSelection[] | null
  >(initialInheritedSelections);
  const prevHscRef = useRef(hsc);
  const prevTruthRef = useRef(initialInheritedSelections);
  useEffect(() => {
    const truthChanged = prevTruthRef.current !== initialInheritedSelections;
    if (prevHscRef.current !== hsc) {
      setInheritedSelections(null);
    } else if (truthChanged && hsc) {
      setInheritedSelections(initialInheritedSelections);
    }
    prevHscRef.current = hsc;
    prevTruthRef.current = initialInheritedSelections;
  }, [hsc, initialInheritedSelections]);

  // Holds the form values when an HSC-off save needs an explicit confirmation
  // because dropping the inherited graph would cascade-delete UBOs. Continue
  // re-enters the save flow with the same values; Cancel resets to null.
  type FormValues = Parameters<Parameters<typeof handleSubmit>[0]>[0];
  const [pendingHscOff, setPendingHscOff] = useState<{
    values: FormValues;
    lostNames: string[];
  } | null>(null);

  const runSave = (values: FormValues) => {
    // Block rather than write a 0-share holding when the class has no
    // authorized count to issue the percentage against.
    const ownedShares = ownedSharesFromPercent(values.ownershipPercent);
    if (ownedShares === null) {
      setError('ownershipPercent', {
        type: 'server',
        message:
          `This ${nouns.classFieldNouns.lowerSingular} has no total set — set it before assigning a percentage.`,
      });
      onError?.(SUBMIT_VALIDATION_TOAST);
      return;
    }

    const proceed = async () => {
      try {
        if (isIndividual) {
          const individual: UpdateIndividualShareholdingPayload = {
            ownedShares,
            hasSignificantControl: values.hasSignificantControl,
            relationshipType: values.relationshipType,
            ...stockClassBody,
            ...(values.hasSignificantControl && !!ubo.controlType
              ? { directControlType: ubo.controlType }
              : {}),
          };
          await updateMutation.mutateAsync({
            stakeholdingId: stakeholder.stakeholdingId,
            body: { type: ShareholdingType.INDIVIDUAL, individual },
          });
        } else {
          const institution: UpdateInstitutionShareholdingPayload = {
            ownedShares,
            hasSignificantControl: values.hasSignificantControl,
            relationshipType: values.relationshipType,
            ...stockClassBody,
            ...(!!parentCorpId
              ? {
                  inheritedUbos: values.hasSignificantControl
                    ? (inheritedSelections ?? [])
                    : [],
                }
              : {}),
          };
          await updateMutation.mutateAsync({
            stakeholdingId: stakeholder.stakeholdingId,
            body: { type: ShareholdingType.INSTITUTION, institution },
          });
        }
        onSuccess?.(`${nouns.singular} updated.`);
        onCancel();
      } catch (err) {
        applyOwnershipApiError(err, setError, onError);
      }
    };
    return proceed();
  };

  const submit = handleSubmit(
    async (values) => {
      const controlTypeOk = ubo.validate(
        isIndividual && values.hasSignificantControl === true,
      );
      if (!controlTypeOk) {
        onError?.(SUBMIT_VALIDATION_TOAST);
        return;
      }

      // Entity row turning HSC off ⇒ the inheritance graph drops. Warn first
      // when that cascade-removes UBOs.
      if (
        isInstitution &&
        !!parentCorpId &&
        stakeholder.hasSignificantControl &&
        values.hasSignificantControl === false
      ) {
        const lost = previewInheritanceLoss(ownership, parentCorpId);
        if (lost.length > 0) {
          setPendingHscOff({ values, lostNames: lost.map((u) => u.name) });
          return;
        }
      }

      await runSave(values);
    },
    () => onError?.(SUBMIT_VALIDATION_TOAST),
  );

  const isControlOnly =
    isIndividual && stakeholder.ownershipPercent === 0 && stakeholder.hasSignificantControl;
  const formTitle = isControlOnly ? 'Edit UBO' : `Edit ${nouns.lowerSingular}`;
  const formSubtitle = isControlOnly
    ? `Update ${stakeholder.name}'s control-only UBO attributes.`
    : `Update ${stakeholder.name}'s stake or compliance attributes.`;

  return (
    <form onSubmit={submit} className="flex flex-col gap-2xl pb-8xl">
      <div className="flex flex-col gap-xs">
        <h1 className="display-xs-bold text-text-primary">{formTitle}</h1>
        <p className="txt-small-regular text-text-secondary">{formSubtitle}</p>
      </div>

      <CollapsibleModuleCard
        title={
          isIndividual
            ? `Individual ${nouns.lowerSingular} details`
            : `Entity ${nouns.lowerSingular} details`
        }
        isExpanded={cardOpen}
        onExpandedChange={setCardOpen}
      >
        <FieldWrapper>
          <Label required>Relationship</Label>
          <Controller
            control={control}
            name="relationshipType"
            render={({ field }) => (
              <SingleCombobox
                canClearSelection={false}
                options={isIndividual ? INDIVIDUAL_RELATIONSHIP_OPTIONS : ENTITY_RELATIONSHIP_OPTIONS}
                value={field.value ?? ''}
                setValue={(v) =>
                  field.onChange(v as OwnershipRelationshipType)
                }
                placeholder="Select relationship"
              />
            )}
          />
          {errors.relationshipType?.message && (
            <InputErrorMessage message={errors.relationshipType.message} />
          )}
        </FieldWrapper>

        <StakeholderStockClassField
          control={control}
          name="stockClassId"
          corporationId={corporationId}
          stagedDraft={stagedStockClass}
          setStagedDraft={setStagedStockClass}
          onError={onError}
        />

        <OwnershipPercentField
          control={control}
          name="ownershipPercent"
          step={percentStep}
          errorMessage={errors.ownershipPercent?.message}
          stockClassName={selectedStockClassName}
        />

        <HscCheckbox
          checked={hsc}
          onCheckedChange={(v) =>
            hscField.onChange(typeof v === 'boolean' ? v : false)
          }
          uboThresholdPercent={uboThreshold}
        />

        {isIndividual && hsc && (
          <UltimateBeneficialOwnerControlTypeSection
            value={ubo.controlType ?? undefined}
            onChange={(v) => {
              ubo.setControlType(v);
              ubo.clearError();
            }}
            error={ubo.error}
          />
        )}

        {isInstitution && hsc && !!parentCorpId && (
          <InheritableUltimateBeneficialOwnersSection
            corporationId={corporationId}
            parentCorporationId={parentCorpId}
            parentEntityName={stakeholder.name}
            selections={inheritedSelections}
            onSelectionsChange={setInheritedSelections}
          />
        )}
      </CollapsibleModuleCard>

      <FormActions
        hideBack
        showCancel
        onCancel={onCancel}
        nextLabel="Save changes"
        isLoading={updateMutation.isPending}
      />

      <Dialog
        open={pendingHscOff !== null}
        onOpenChange={(open) => !open && setPendingHscOff(null)}
      >
        <DialogContent className="sm:max-w-[528px]">
          <DialogHeader>
            <DialogTitle>Remove {stakeholder.name}&apos;s significant control?</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-md p-3xl">
            <p className="txt-small-regular text-text-secondary">
              {stakeholder.name} will no longer be marked as a significant controller.
            </p>
            {pendingHscOff && (
              <p className="txt-small-regular text-text-secondary">
                {formatPersonList(pendingHscOff.lostNames)}{' '}
                {pendingHscOff.lostNames.length === 1 ? 'is' : 'are'} listed as UBO
                {pendingHscOff.lostNames.length === 1 ? '' : 's'} of this entity only
                through {stakeholder.name}. {pendingHscOff.lostNames.length === 1 ? 'They' : 'They'}{' '}
                will also be removed as UBO
                {pendingHscOff.lostNames.length === 1 ? '' : 's'}.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="secondary"
              size="md"
              type="button"
              onClick={() => setPendingHscOff(null)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive-primary"
              size="md"
              type="button"
              onClick={() => {
                const values = pendingHscOff?.values;
                setPendingHscOff(null);
                if (values) void runSave(values);
              }}
              isLoading={updateMutation.isPending}
              loadingLabel="Saving…"
            >
              Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </form>
  );
};
